import {
  comprobarRaiz,
  crearErrorDocx,
  elementosPorNombreLocal,
  parsearXmlDocx,
} from './xml.js'

export const RELATIONSHIPS_NAMESPACE =
  'http://schemas.openxmlformats.org/package/2006/relationships'

const RELATIONSHIP_BASES = [
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships/',
  'http://purl.oclc.org/ooxml/officeDocument/relationships/',
]

const TIPOS_DOCUMENTO_PRINCIPAL = new Set(
  RELATIONSHIP_BASES.map((base) => `${base}officeDocument`)
)

const TIPOS_PARTE_TEXTO = new Set(
  RELATIONSHIP_BASES.flatMap((base) =>
    ['header', 'footer', 'footnotes', 'endnotes', 'comments'].map((tipo) => `${base}${tipo}`)
  )
)

function rutaRelaciones(partName) {
  const ultimoSeparador = partName.lastIndexOf('/')
  const directorio = ultimoSeparador === -1 ? '' : partName.slice(0, ultimoSeparador + 1)
  const nombre = partName.slice(ultimoSeparador + 1)
  return `${directorio}_rels/${nombre}.rels`
}

function normalizarRutaParte(origen, target) {
  const rutaBase = target.startsWith('/')
    ? []
    : origen
        .split('/')
        .slice(0, -1)
        .filter(Boolean)
  const segmentos = target.replace(/^\/+/, '').split('/')

  for (const segmento of segmentos) {
    if (!segmento || segmento === '.') continue
    if (segmento === '..') {
      if (!rutaBase.length) {
        throw crearErrorDocx(
          'docx_relationship_target_invalid',
          'Una relación intenta salir de la raíz del paquete DOCX',
          { origen, target }
        )
      }
      rutaBase.pop()
      continue
    }
    rutaBase.push(segmento)
  }

  return rutaBase.join('/')
}

function leerRelaciones(zip, partName, required) {
  const relsPath = rutaRelaciones(partName)
  const archivo = zip.file(relsPath)
  if (!archivo) {
    if (!required) return []
    throw crearErrorDocx('docx_relationships_missing', 'Falta una parte de relaciones obligatoria', {
      partName,
      relsPath,
    })
  }

  const documento = parsearXmlDocx(archivo.asText(), relsPath)
  comprobarRaiz(documento, RELATIONSHIPS_NAMESPACE, 'Relationships', relsPath)
  return elementosPorNombreLocal(documento, RELATIONSHIPS_NAMESPACE, 'Relationship').map(
    (elemento) => ({
      id: elemento.getAttribute('Id'),
      type: elemento.getAttribute('Type'),
      target: elemento.getAttribute('Target'),
      targetMode: elemento.getAttribute('TargetMode'),
    })
  )
}

export function obtenerRutaRelaciones(partName) {
  return rutaRelaciones(partName)
}

export function obtenerPartesTextoDocx(zip) {
  const relacionesRaiz = leerRelaciones(zip, '', true)
  const relacionesDocumento = relacionesRaiz.filter(
    (relacion) =>
      TIPOS_DOCUMENTO_PRINCIPAL.has(relacion.type) && relacion.targetMode !== 'External'
  )

  if (relacionesDocumento.length !== 1 || !relacionesDocumento[0].target) {
    throw crearErrorDocx(
      'docx_main_document_relationship_invalid',
      'El DOCX debe contener exactamente una relación interna al documento principal',
      { relationshipsFound: relacionesDocumento.length }
    )
  }

  const documentoPrincipal = normalizarRutaParte('', relacionesDocumento[0].target)
  if (!zip.file(documentoPrincipal)) {
    throw crearErrorDocx('docx_main_document_missing', 'No existe el documento principal del DOCX', {
      partName: documentoPrincipal,
    })
  }

  const partes = [documentoPrincipal]
  const relaciones = leerRelaciones(zip, documentoPrincipal, false)
  for (const relacion of relaciones) {
    if (!TIPOS_PARTE_TEXTO.has(relacion.type) || relacion.targetMode === 'External') continue
    if (!relacion.target) {
      throw crearErrorDocx('docx_relationship_target_missing', 'Una relación no tiene destino', {
        sourcePart: documentoPrincipal,
        relationshipId: relacion.id,
      })
    }

    const partName = normalizarRutaParte(documentoPrincipal, relacion.target)
    if (!zip.file(partName)) {
      throw crearErrorDocx('docx_related_part_missing', 'No existe una parte relacionada del DOCX', {
        sourcePart: documentoPrincipal,
        relationshipId: relacion.id,
        partName,
      })
    }
    if (!partes.includes(partName)) partes.push(partName)
  }

  return partes
}

export function obtenerTargetRelativo(sourcePart, targetPart) {
  const origen = sourcePart.split('/').slice(0, -1)
  const destino = targetPart.split('/')
  let comunes = 0

  while (
    comunes < origen.length &&
    comunes < destino.length &&
    origen[comunes] === destino[comunes]
  ) {
    comunes++
  }

  return [...origen.slice(comunes).map(() => '..'), ...destino.slice(comunes)].join('/')
}
